/**
 * `note` — a short block the graph composed: what changed in the plan, a
 * confirmation, a warning.
 *
 * `tone: "emergency"` (112/103 — an injury, a lost child) is the ONE hard
 * layout commitment in this frontend. It is pinned across both panes and both
 * mobile tabs by `App`, appears with no animation at all, and is never folded
 * away by a counter. See §4.5 — the reasoning is that «did not see it» costs
 * more here than everything else in this project put together.
 */

import type { NoteCardProps } from '../generated/schemas';
import { cn } from '../../lib/cn';
import { stripEmoji as plain } from '../../lib/emoji';

/**
 * The catalogue's own vocabulary — `info | warn | emergency`, adopted rather
 * than translated. A frontend that renamed `warn` to `warning` for its own
 * comfort is a frontend that fails the museum's closure notice the day the two
 * spellings drift.
 */
const GLYPH: Record<NoteCardProps['tone'], string> = {
  info: 'i',
  warn: '!',
  emergency: '!',
};

/** What the mark says, for a reader who does not see it. */
const TONE_WORD: Record<NoteCardProps['tone'], string> = {
  info: 'Информация',
  warn: 'Внимание',
  emergency: 'Срочно',
};

export default function NoteCard({ props }: { props: NoteCardProps }) {
  return (
    <article className={cn('card', 'card--note', `card--note-${props.tone}`)}>
      <div className="note">
        <span className="note__glyph" aria-hidden="true">
          {GLYPH[props.tone]}
        </span>
        <div className="note__body">
          <span className="visually-hidden">{TONE_WORD[props.tone]}: </span>
          {props.title ? <h3 className="note__title">{plain(props.title)}</h3> : null}
          <p className="note__text">{plain(props.text)}</p>
        </div>
      </div>
    </article>
  );
}
